import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UIProvider } from "@/context/UIContext";
import Layout, { NAV } from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Devices from "@/pages/Devices";
import StatusPage from "@/pages/StatusPage";
import Dashboard from "@/pages/Dashboard";
import Inward from "@/pages/Inward";
import JobDetail from "@/pages/JobDetail";
import Outward from "@/pages/Outward";
import Projects from "@/pages/Projects";
import ProjectBoard from "@/pages/ProjectBoard";
import Inventory from "@/pages/Inventory";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Workload from "@/pages/Workload";
import Notifications from "@/pages/Notifications";
import Audit from "@/pages/Audit";
import UsersPage from "@/pages/Users";
import Permissions from "@/pages/Permissions";
import StorageSettings from "@/pages/StorageSettings";
import { Radar } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="flex h-screen items-center justify-center bg-background text-primary">
        <Radar size={32} className="animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Guard({ perm, admin, children }) {
  const { user, can } = useAuth();
  const allowed = (n) => (n.adminOnly ? user.role === "admin" : can(n.perm));
  if (admin ? user.role === "admin" : can(perm)) return children;
  const first = NAV.find(allowed);
  if (first) return <Navigate to={first.to} replace />;
  return <div className="p-10 text-center text-sm text-muted-foreground" data-testid="no-access">You don't have access to any pages. Contact your Manager.</div>;
}

const g = (perm, el) => <Guard perm={perm}>{el}</Guard>;

function App() {
  return (
    <UIProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors theme="system" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/status/:token" element={<StatusPage />} />
            <Route
              path="/"
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route index element={g("nav.dashboard", <Dashboard />)} />
              <Route path="inward" element={g("nav.inward", <Inward />)} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="outward" element={g("nav.outward", <Outward />)} />
              <Route path="projects" element={g("nav.projects", <Projects />)} />
              <Route path="projects/:id" element={g("nav.projects", <ProjectBoard />)} />
              <Route path="inventory" element={g("nav.inventory", <Inventory />)} />
              <Route path="purchase-orders" element={g("nav.purchase_orders", <PurchaseOrders />)} />
              <Route path="workload" element={g("nav.workload", <Workload />)} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="devices" element={<Devices />} />
              <Route path="audit" element={g("nav.audit", <Audit />)} />
              <Route path="users" element={g("nav.users", <UsersPage />)} />
              <Route path="permissions" element={<Guard admin><Permissions /></Guard>} />
              <Route path="settings/storage" element={g("nav.storage", <StorageSettings />)} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </UIProvider>
  );
}

export default App;
